import moment from 'moment'

// Categorical palette: the validated 8-slot order. Verified against the #ffffff
// chart surface -- lightness band, chroma floor, CVD separation and normal-vision
// floor all pass. Three slots (aqua, yellow, magenta) sit below 3:1 contrast, so
// the relief rule applies: every chart using them ships a table view.
//
// Slots are assigned by entity identity and never cycled. A 9th series folds into
// OTHER rather than generating a new hue, which would be indistinguishable under
// colour-vision deficiency.
export const PALETTE = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948']
export const OTHER_COLOR = '#898781'

// The entity an action operates on. Entities carry the colour on the Users tab --
// there are exactly 7, so each gets a permanent slot and a colour always means the
// same thing across every chart.
export const ENTITIES = [
    { value: 'INCIDENT', label: 'Incidents' },
    { value: 'LIABILITY_CLAIM', label: 'Liability Claims' },
    { value: 'SUBROGATION_CLAIM', label: 'Subro Claims' },
    { value: 'PARTY', label: 'Parties' },
    { value: 'PARTY_PEOPLE', label: 'Party People' },
    { value: 'PARTY_VEHICLE', label: 'Party Vehicles' },
    { value: 'DOCUMENT', label: 'Documents' },
]

export const entityColor = (entityValue) => {
    const index = ENTITIES.findIndex((entity) => entity.value === entityValue)
    return index === -1 ? OTHER_COLOR : PALETTE[index]
}

export const entityLabel = (entityValue) =>
    ENTITIES.find((entity) => entity.value === entityValue)?.label || entityValue

// Every action the backend reports on.
//
// `metric` mirrors the two count columns: creations count distinct objects, updates
// count history events (several fire per object).
export const ACTIONS = [
    { value: 'INCIDENT_CREATED', label: 'Incident Created', entity: 'INCIDENT', metric: 'objectsCount', mode: 'created' },
    { value: 'INCIDENT_UPDATED', label: 'Incident Updated', entity: 'INCIDENT', metric: 'eventsCount', mode: 'updated' },
    { value: 'LIABILITY_CLAIM_CREATED', label: 'Liability Claim Created', entity: 'LIABILITY_CLAIM', metric: 'objectsCount', mode: 'created' },
    { value: 'LIABILITY_CLAIM_UPDATED', label: 'Liability Claim Updated', entity: 'LIABILITY_CLAIM', metric: 'eventsCount', mode: 'updated' },
    { value: 'SUBROGATION_CLAIM_CREATED', label: 'Subro Claim Created', entity: 'SUBROGATION_CLAIM', metric: 'objectsCount', mode: 'created' },
    { value: 'SUBROGATION_CLAIM_UPDATED', label: 'Subro Claim Updated', entity: 'SUBROGATION_CLAIM', metric: 'eventsCount', mode: 'updated' },
    { value: 'PARTY_CREATED', label: 'Party Created', entity: 'PARTY', metric: 'objectsCount', mode: 'created' },
    { value: 'PARTY_UPDATED', label: 'Party Updated', entity: 'PARTY', metric: 'eventsCount', mode: 'updated' },
    { value: 'PARTY_PEOPLE_CREATED', label: 'Party Person Added', entity: 'PARTY_PEOPLE', metric: 'objectsCount', mode: 'created' },
    { value: 'PARTY_PEOPLE_UPDATED', label: 'Party Person Updated', entity: 'PARTY_PEOPLE', metric: 'eventsCount', mode: 'updated' },
    { value: 'PARTY_VEHICLE_CREATED', label: 'Party Vehicle Added', entity: 'PARTY_VEHICLE', metric: 'objectsCount', mode: 'created' },
    { value: 'PARTY_VEHICLE_UPDATED', label: 'Party Vehicle Updated', entity: 'PARTY_VEHICLE', metric: 'eventsCount', mode: 'updated' },
    { value: 'DOCUMENT_UPLOADED', label: 'Document Uploaded', entity: 'DOCUMENT', metric: 'objectsCount', mode: 'created' },
    { value: 'DOCUMENT_UPDATED', label: 'Document Updated', entity: 'DOCUMENT', metric: 'eventsCount', mode: 'updated' },
]

export const ACTION_OPTIONS = ACTIONS.map((action) => ({ value: action.value, label: action.label }))

const ACTIONS_BY_VALUE = ACTIONS.reduce((acc, action) => ({ ...acc, [action.value]: action }), {})

export const actionMeta = (actionValue) => ACTIONS_BY_VALUE[actionValue]

export const actionLabel = (actionValue) => ACTIONS_BY_VALUE[actionValue]?.label || actionValue

/**
 * The count a row contributes, picking the column that matches the action's
 * semantics: distinct objects for creations, history events for updates.
 */
export const rowCount = (row) => {
    const meta = ACTIONS_BY_VALUE[row.action]
    if (!meta) return 0
    return Number(row[meta.metric]) || 0
}

/**
 * Narrow rows by any combination of action, branch, user and user id.
 *
 * An empty list for a dimension means "no filter on it". Dimensions intersect,
 * so selecting a group and a user shows only that user, and only if they are in
 * the group.
 *
 * `userIds` is separate from `users` on purpose: the group filter resolves to
 * user ids, because display names are not unique and the report's name formula
 * does not match `UserNode.resolve_name` in every case. Ids are compared as
 * strings so a numeric row id and a GraphQL `pk` line up.
 */
export const filterRows = (rows, { actions = [], branches = [], users = [], userIds = [] } = {}) => {
    const actionSet = actions.length ? new Set(actions) : null
    const branchSet = branches.length ? new Set(branches) : null
    const userSet = users.length ? new Set(users) : null
    const userIdSet = userIds.length ? new Set(userIds.map(String)) : null

    return rows.filter((row) => {
        if (actionSet && !actionSet.has(row.action)) return false
        if (branchSet && !branchSet.has(row.branchName)) return false
        if (userSet && !userSet.has(row.userName)) return false
        if (userIdSet && !userIdSet.has(String(row.userId))) return false
        return true
    })
}

/** Sum `rowCount` into a plain object keyed by `keyFn`. */
export const totalsBy = (rows, keyFn) =>
    rows.reduce((acc, row) => {
        const key = keyFn(row)
        acc[key] = (acc[key] || 0) + rowCount(row)
        return acc
    }, {})

/** Keys of a totals object, largest first. */
export const rankKeys = (totals) => Object.keys(totals).sort((a, b) => totals[b] - totals[a])

/**
 * Users x day grid for the heatmap. Days with no activity are still emitted so
 * gaps in someone's week are visible rather than collapsed away.
 */
export const buildHeatmap = (rows, startDate, endDate) => {
    const days = []
    const cursor = moment(startDate)
    const last = moment(endDate)
    while (cursor.isSameOrBefore(last, 'day')) {
        days.push(cursor.format('YYYY-MM-DD'))
        cursor.add(1, 'day')
        if (days.length > 366) break
    }

    const totals = totalsBy(rows, (row) => `${row.userName}|${row.eventDate}`)
    const userTotals = totalsBy(rows, (row) => row.userName)
    const users = rankKeys(userTotals)
    const peak = Math.max(0, ...Object.values(totals))

    const grid = users.map((userName) => ({
        userName,
        total: userTotals[userName],
        cells: days.map((day) => ({ day, count: totals[`${userName}|${day}`] || 0 })),
    }))

    return { days, grid, peak }
}

/**
 * Bucket a count onto a 0-6 step of the sequential blue ramp. Returned as a level
 * rather than a colour so the cell can wear a class instead of an inline style.
 */
export const heatLevel = (count, peak) => {
    if (!count) return 0
    if (peak <= 0) return 0
    return Math.max(1, Math.min(6, Math.ceil((count / peak) * 6)))
}

/** Daily totals per entity, for the trend line. */
export const buildTrend = (rows, startDate, endDate) => {
    const days = []
    const cursor = moment(startDate)
    const last = moment(endDate)
    while (cursor.isSameOrBefore(last, 'day')) {
        days.push(cursor.format('YYYY-MM-DD'))
        cursor.add(1, 'day')
        if (days.length > 366) break
    }

    const totals = totalsBy(rows, (row) => `${row.eventDate}|${actionMeta(row.action)?.entity}`)
    const presentEntities = ENTITIES.filter((entity) => days.some((day) => totals[`${day}|${entity.value}`]))

    const data = days.map((day) => {
        const record = { day, label: moment(day).format('MMM D') }
        presentEntities.forEach((entity) => {
            record[entity.value] = totals[`${day}|${entity.value}`] || 0
        })
        return record
    })

    const series = presentEntities.map((entity) => ({
        key: entity.value,
        label: entity.label,
        color: entityColor(entity.value),
    }))

    return { data, series }
}

/**
 * Leaderboard shaping: one record per user with a flat `created_ENTITY` /
 * `updated_ENTITY` field per entity, sorted by total effort descending.
 */
export const buildLeaderboard = (rows) => {
    const byUser = {}

    rows.forEach((row) => {
        const meta = actionMeta(row.action)
        if (!meta) return

        if (!byUser[row.userName]) byUser[row.userName] = { userName: row.userName, total: 0 }
        const field = `${meta.mode}_${meta.entity}`
        byUser[row.userName][field] = (byUser[row.userName][field] || 0) + rowCount(row)
        byUser[row.userName].total += rowCount(row)
    })

    const data = Object.values(byUser).sort((a, b) => b.total - a.total)

    // Only entities that actually occur get a legend entry and a colour.
    const presentEntities = ENTITIES.filter((entity) =>
        data.some((record) => record[`created_${entity.value}`] || record[`updated_${entity.value}`])
    )
    const series = presentEntities.map((entity) => ({
        key: entity.value,
        label: entity.label,
        color: entityColor(entity.value),
    }))

    return { data, series }
}

/** DataTable rows for the leaderboard, split into created/updated columns. */
export const buildLeaderboardTable = (data, series) => {
    const columns = series.flatMap((entity) => [
        { key: `created_${entity.key}`, label: `${entity.label} created`, color: entity.color },
        { key: `updated_${entity.key}`, label: `${entity.label} updated`, color: entity.color },
    ])

    const rows = data.map((record) => ({
        name: record.userName,
        total: record.total || 0,
        values: columns.reduce((acc, column) => ({ ...acc, [column.key]: record[column.key] || 0 }), {}),
    }))

    return { rows, columns }
}

/* -------------------------------------------------------------------------
   Generic shaping
   ------------------------------------------------------------------------- */

export const daysBetween = (startDate, endDate) => {
    const days = []
    const cursor = moment(startDate)
    const last = moment(endDate)
    while (cursor.isSameOrBefore(last, 'day')) {
        days.push(cursor.format('YYYY-MM-DD'))
        cursor.add(1, 'day')
        if (days.length > 366) break
    }
    return days
}

/**
 * Group rows by anything (user, branch) with one numeric field per action,
 * sorted by total descending.
 *
 * Records are keyed by the action name itself, so a chart series key *is* an
 * action -- no second naming scheme to keep in sync.
 */
export const buildActionMix = (rows, { groupKeyFn, actions, groupKey = 'name' }) => {
    const wanted = new Set(actions)
    const byGroup = {}

    rows.forEach((row) => {
        if (!wanted.has(row.action)) return
        const key = groupKeyFn(row)
        if (!byGroup[key]) byGroup[key] = { [groupKey]: key, total: 0 }
        byGroup[key][row.action] = (byGroup[key][row.action] || 0) + rowCount(row)
        byGroup[key].total += rowCount(row)
    })

    return Object.values(byGroup).sort((a, b) => b.total - a.total)
}

/** One record per day with a field per action, gaps included. */
export const buildActionTrend = (rows, startDate, endDate, actions) => {
    const days = daysBetween(startDate, endDate)
    const wanted = new Set(actions)
    const totals = totalsBy(
        rows.filter((row) => wanted.has(row.action)),
        (row) => `${row.eventDate}|${row.action}`
    )

    return days.map((day) => {
        const record = { day, label: moment(day).format('MMM D') }
        actions.forEach((action) => {
            record[action] = totals[`${day}|${action}`] || 0
        })
        return record
    })
}

/** Total plus a per-day series for one action, for a stat tile. */
export const buildActionSpark = (rows, startDate, endDate, action) => {
    const matching = rows.filter((row) => row.action === action)
    const totals = totalsBy(matching, (row) => row.eventDate)

    return {
        total: matching.reduce((sum, row) => sum + rowCount(row), 0),
        spark: daysBetween(startDate, endDate).map((day) => totals[day] || 0),
    }
}

/** Rows -> DataTable rows, given series whose keys index the record. */
export const buildMixTableRows = (data, series, groupKey = 'name') =>
    data.map((record) => ({
        name: record[groupKey],
        total: record.total || 0,
        values: series.reduce((acc, item) => ({ ...acc, [item.key]: record[item.key] || 0 }), {}),
    }))

/* -------------------------------------------------------------------------
   Incidents tab
   -------------------------------------------------------------------------
   Colour carries the activity type -- three series, well inside the range
   where hue alone is comfortable, and slots 1-3 are the trio that validates
   under every pairing. Branch and user are carried by position instead.
   ------------------------------------------------------------------------- */

export const INCIDENT_ACTIVITY = {
    created: { key: 'INCIDENT_CREATED', label: 'Incidents created', color: PALETTE[0] },
    updated: { key: 'INCIDENT_UPDATED', label: 'Incident updates', color: PALETTE[1] },
    documents: { key: 'DOCUMENT_UPLOADED', label: 'Documents uploaded', color: PALETTE[2] },
}

// Incidents and documents are never plotted against each other. They are
// different units -- an incident is a case, a document is a file attached to one
// -- so stacking them in a bar or running them as two lines on one axis would
// imply a comparison that does not exist. Documents get their own chart.
export const INCIDENT_TREND_SERIES = [INCIDENT_ACTIVITY.created]

// Two bars per person: incidents opened, and edit events on incidents. Colour
// separates the two because they are different measures of the same object.
export const INCIDENT_WORKLOAD_SERIES = [
    { ...INCIDENT_ACTIVITY.created, stack: 'created' },
    { ...INCIDENT_ACTIVITY.updated, stack: 'updated' },
]

export const INCIDENT_BRANCH_SERIES = [INCIDENT_ACTIVITY.created]

// Its own chart, its own colour -- never a segment beside incident counts.
export const DOCUMENT_SERIES = [INCIDENT_ACTIVITY.documents]

/** Stat-tile numbers for the Incidents tab. */
export const buildIncidentKpis = (rows, startDate, endDate) => {
    const activityActions = Object.values(INCIDENT_ACTIVITY).map((item) => item.key)
    const scoped = rows.filter((row) => activityActions.includes(row.action))

    const byUser = totalsBy(scoped, (row) => row.userName)
    const topUser = rankKeys(byUser)[0]

    return {
        created: buildActionSpark(rows, startDate, endDate, INCIDENT_ACTIVITY.created.key),
        updated: buildActionSpark(rows, startDate, endDate, INCIDENT_ACTIVITY.updated.key),
        documents: buildActionSpark(rows, startDate, endDate, INCIDENT_ACTIVITY.documents.key),
        topUser: { name: topUser || '--', count: topUser ? byUser[topUser] : 0 },
    }
}

/* -------------------------------------------------------------------------
   Claims tab
   -------------------------------------------------------------------------
   The subject of this tab is the claim type, so colour carries liability vs
   subro throughout -- two series, which is comfortable for every reader and
   leaves branch and user free to be position rather than hue.
   ------------------------------------------------------------------------- */

export const CLAIM_TYPES = [
    { key: 'LIABILITY_CLAIM', label: 'Liability', color: PALETTE[0], slot: 1 },
    { key: 'SUBROGATION_CLAIM', label: 'Subro', color: PALETTE[1], slot: 2 },
]

const CLAIM_ACTIONS = ACTIONS.filter((action) => CLAIM_TYPES.some((type) => type.key === action.entity))

export const CLAIM_ACTION_VALUES = CLAIM_ACTIONS.map((action) => action.value)

const claimRowsOnly = (rows) => rows.filter((row) => CLAIM_ACTION_VALUES.includes(row.action))

/** Daily opened-claim counts per claim type, plus the sparkline series. */
export const buildClaimsTrend = (rows, startDate, endDate, mode = 'created') => {
    const days = daysBetween(startDate, endDate)
    const scoped = claimRowsOnly(rows).filter((row) => actionMeta(row.action)?.mode === mode)
    const totals = totalsBy(scoped, (row) => `${row.eventDate}|${actionMeta(row.action).entity}`)

    const data = days.map((day) => {
        const record = { day, label: moment(day).format('MMM D') }
        CLAIM_TYPES.forEach((type) => {
            record[type.key] = totals[`${day}|${type.key}`] || 0
        })
        return record
    })

    return { data, series: CLAIM_TYPES }
}

/** Per-user created and updated counts, split by claim type. */
export const buildClaimsByUser = (rows) => {
    const scoped = claimRowsOnly(rows)
    const byUser = {}

    scoped.forEach((row) => {
        const meta = actionMeta(row.action)
        if (!byUser[row.userName]) byUser[row.userName] = { userName: row.userName, total: 0, created: 0, updated: 0 }

        const record = byUser[row.userName]
        const field = `${meta.mode}_${meta.entity}`
        record[field] = (record[field] || 0) + rowCount(row)
        record[meta.mode] += rowCount(row)
        record.total += rowCount(row)
    })

    return { data: Object.values(byUser).sort((a, b) => b.total - a.total), series: CLAIM_TYPES }
}

/** Per-branch claim volume, split by claim type -- the part-to-whole view. */
export const buildClaimsByBranch = (rows, mode = 'created') => {
    const scoped = claimRowsOnly(rows).filter((row) => actionMeta(row.action)?.mode === mode)
    const byBranch = {}

    scoped.forEach((row) => {
        const entity = actionMeta(row.action).entity
        if (!byBranch[row.branchName]) byBranch[row.branchName] = { branchName: row.branchName, total: 0 }
        byBranch[row.branchName][entity] = (byBranch[row.branchName][entity] || 0) + rowCount(row)
        byBranch[row.branchName].total += rowCount(row)
    })

    return { data: Object.values(byBranch).sort((a, b) => b.total - a.total), series: CLAIM_TYPES }
}

/** Stat-tile numbers for the Claims tab, each with a daily sparkline. */
export const buildClaimsKpis = (rows, startDate, endDate) => {
    const days = daysBetween(startDate, endDate)
    const scoped = claimRowsOnly(rows)

    const sparkFor = (entity, mode) => {
        const matching = scoped.filter(
            (row) => actionMeta(row.action).entity === entity && actionMeta(row.action).mode === mode
        )
        const totals = totalsBy(matching, (row) => row.eventDate)
        return {
            total: matching.reduce((sum, row) => sum + rowCount(row), 0),
            spark: days.map((day) => totals[day] || 0),
        }
    }

    const liability = sparkFor('LIABILITY_CLAIM', 'created')
    const subro = sparkFor('SUBROGATION_CLAIM', 'created')

    const updatedRows = scoped.filter((row) => actionMeta(row.action).mode === 'updated')
    const updateTotals = totalsBy(updatedRows, (row) => row.eventDate)

    const byUser = totalsBy(scoped, (row) => row.userName)
    const topUser = rankKeys(byUser)[0]

    return {
        liability,
        subro,
        updates: {
            total: updatedRows.reduce((sum, row) => sum + rowCount(row), 0),
            spark: days.map((day) => updateTotals[day] || 0),
        },
        topUser: { name: topUser || '--', count: topUser ? byUser[topUser] : 0 },
    }
}

/** Headline numbers for the Users tab. Dates are optional; they add a sparkline. */
export const buildKpis = (rows, startDate, endDate) => {
    const total = rows.reduce((sum, row) => sum + rowCount(row), 0)
    const byUser = totalsBy(rows, (row) => row.userName)
    const byDay = totalsBy(rows, (row) => row.eventDate)

    const topUser = rankKeys(byUser)[0]
    const busiestDay = rankKeys(byDay)[0]

    return {
        totalSpark: startDate && endDate ? daysBetween(startDate, endDate).map((day) => byDay[day] || 0) : [],
        totalActions: total,
        activeUsers: Object.keys(byUser).length,
        busiestDay: busiestDay ? moment(busiestDay).format('MMM D') : '--',
        busiestDayCount: busiestDay ? byDay[busiestDay] : 0,
        topUser: topUser || '--',
        topUserCount: topUser ? byUser[topUser] : 0,
    }
}

export const toOptions = (values) => values.map((value) => ({ label: value, value }))

/**
 * Map a palette hex back to its slot number so DOM elements (legend and tooltip
 * swatches) can wear a `viz-swatch--n` class instead of an inline background.
 *
 * Recharts hands us the resolved fill colour in its tooltip payload, and every
 * colour we ever pass in comes from PALETTE or OTHER_COLOR, so this is total.
 */
export const slotForColor = (color) => {
    const index = PALETTE.indexOf(color)
    return index === -1 ? 'other' : index + 1
}
